import { Config } from '../config';
import { Fleet } from '../interfaces';
import { EventsFilter } from '../interfaces/eventsFilter';
import { EventsService } from './events.service';
import { HttpService } from './http.service';

const path = `${Config.API}/api/v1/reports`;
const pathV2 = `${Config.API}/api/v2/reports`;

const sortReportFilesByMonthAndYear = (reports: string[]): string[] => {
  const numberRegex = /([0-9]{4})/gms;
  const monthRegex = /([A-Z]{1}[a-z]*)/gms;
  const reportCopy = [...reports];

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  reportCopy.sort((a, b) => {
    const yearA: number = +a.match(numberRegex)[0];
    const yearB: number = +b.match(numberRegex)[0];

    const monthAString = a.match(monthRegex)[0];
    const monthBString = b.match(monthRegex)[0];
    const monthA = months.indexOf(monthAString) ?? 0;
    const monthB = months.indexOf(monthBString) ?? 0;

    if (yearA == yearB) {
      return monthA - monthB;
    }

    return yearA - yearB;
  });
  return reportCopy;
};

export abstract class ReportsService {
  public static postNewStaticReport = (fleet: Fleet, month: number, year: number): Promise<boolean> => {
    const queryString = new URLSearchParams({ month: month.toString(), year: year.toString() }).toString();

    return HttpService.fetchAuthorized(`${path}/csv/monthly/generate?${queryString}`, {
      method: 'POST',
    })
      .then(() => true)
      .catch(() => false);
  };

  public static postNewStaticPermittedReport = (fleet: Fleet, month: number, year: number): Promise<boolean> => {
    const queryString = new URLSearchParams({ month: month.toString(), year: year.toString() }).toString();

    return HttpService.fetchAuthorized(`${path}/csv/permittedlist/monthly?${queryString}`, {
      method: 'POST',
    })
      .then(() => true)
      .catch(() => false);
  };

  public static postReport = (filter: EventsFilter, fleet: Fleet): Promise<{ url: string; name: string }> => {
    return HttpService.fetchAuthorized(`${pathV2}/csv/eventsdetails`, {
      method: 'POST',
      body: JSON.stringify({ ...EventsService.sanitizeFilters(filter, fleet.timezone) }),
    }).then((res) => {
      return {
        url: `${pathV2}/csv/eventsdetails?fleet_id=${fleet.uuid}&report_name=${res.Report}`,
        name: res.Report,
      };
    });
  };

  public static triggerDbtCloudJob = (reportPath: string): Promise<unknown> => {
    return HttpService.fetchAuthorized(`${path}/csv/all/generate`, {
      method: 'POST',
      body: JSON.stringify({ report_path: reportPath }),
    });
  };

  public static postFatReport = (filter: EventsFilter, fleet: Fleet): Promise<{ presigned_url: string; name: string }> => {
    return HttpService.fetchAuthorized(`${pathV2}/fat?fleet_id=${fleet.uuid}`, {
      method: 'POST',
      body: JSON.stringify({ ...EventsService.sanitizeFilters(filter, fleet.timezone) }),
    });
  };

  public static getAvailableReports = (fleet: Fleet): Promise<{ reports: string[] }> => {
    return HttpService.fetchAuthorized(`${path}/csv/monthly?fleet_id=${fleet.uuid}`, {
      method: 'GET',
    }).then((res) => {
      return { reports: sortReportFilesByMonthAndYear(res?.reports ?? []) };
    });
  };

  public static getAllAvailableReports = (fleet: Fleet): Promise<{ reports: string[] }> => {
    return HttpService.fetchAuthorized(`${path}/csv/all?fleet_id=${fleet.uuid}`, {
      method: 'GET',
    }).then((res) => ({
      reports: sortReportFilesByMonthAndYear(res?.reports ?? []),
    }));
  };

  public static getAvailablePermittedReports = (fleet: Fleet): Promise<{ reports: string[] }> => {
    return HttpService.fetchAuthorized(`${path}/csv/permittedlist/monthly?fleet_id=${fleet.uuid}`, {
      method: 'GET',
    }).then((res) => {
      return { reports: sortReportFilesByMonthAndYear(res?.reports ?? []) };
    });
  };

  public static deleteReport = (reportName: string, fleet: Fleet): Promise<{ reports: string[] }> => {
    return HttpService.fetchAuthorized(`${path}/csv/monthly/report?fleet_id=${fleet.uuid}&report_name=${reportName}`, {
      method: 'DELETE',
    }).then((res) => {
      return res;
    });
  };

  public static deletePermittedListReport = (reportName: string, fleet: Fleet): Promise<{ reports: string[] }> => {
    return HttpService.fetchAuthorized(`${path}/csv/permittedlist/monthly?fleet_id=${fleet.uuid}&report_name=${reportName}`, {
      method: 'DELETE',
    }).then((res) => {
      return res;
    });
  };
}
